import {SET_FULL_REPORT, RESET_USER} from '../actions/types';
import {AnyAction} from 'redux';

// Enhanced state type with class reports cache
export type ReportState = {
  fullReport: any[];
  reportsByStatisticId: Record<string, any>; // Cache by statistic ID
  reportsByClassId: Record<string, any>; // Cache by class ID
  schoolAttendanceByMonth: Record<string, any>; // Cache by school-month key
  schoolReportsSummaryByMonth: Record<string, any>; // Cache school reports summary by school-month key
  schoolAggregatedData: Record<string, any>; // Cache aggregated performance by schoolId
  classStatisticsByClassId: Record<string, any>; // Cache class statistics (phonetics & syllables) by classId
  selectedClassMonthlyReportArr: any[]; // Array of monthly school reports with selected flag
  classMonthlyReportsCache: any[]; // Array of class monthly reports by month with selected flag (for school-level grid)
  classMonthlyReportsByMonth: Record<string, any>; // Cache for individual class monthly reports by classId-month key (excludes current month)
  studentMonthlyReportsByMonth: Record<string, any>; // Cache for individual student monthly reports by studentId-month key (excludes current month)
};

export const initialState: ReportState = {
  fullReport: [],
  reportsByStatisticId: {},
  reportsByClassId: {}, // Initialized empty cache for class reports
  schoolAttendanceByMonth: {}, // Initialized empty cache for school attendance reports
  schoolReportsSummaryByMonth: {}, // Initialized empty cache for school reports summary
  schoolAggregatedData: {}, // Initialized empty cache for school aggregated performance
  classStatisticsByClassId: {}, // Initialized empty cache for class statistics
  selectedClassMonthlyReportArr: [], // Initialized empty array for monthly school reports
  classMonthlyReportsCache: [], // Initialized empty array for class monthly reports cache (for school-level grid)
  classMonthlyReportsByMonth: {}, // Cache for individual class monthly reports (excludes current month)
  studentMonthlyReportsByMonth: {}, // Cache for individual student monthly reports (excludes current month)
};

const fullReport = (
  state: ReportState = initialState,
  action: AnyAction,
): ReportState => {
  // Ensure selectedClassMonthlyReportArr is always an array (migration fix)
  if (state.selectedClassMonthlyReportArr === undefined) {
    state = {
      ...state,
      selectedClassMonthlyReportArr: [],
    };
  }

  // Ensure classMonthlyReportsCache is always an array (migration fix)
  if (state.classMonthlyReportsCache === undefined) {
    state = {
      ...state,
      classMonthlyReportsCache: [],
    };
  }

  switch (action.type) {
    case SET_FULL_REPORT:
      // Bulk replace the entire report list
      return {
        ...state,
        fullReport: action.payload,
      };

    case 'STORE_REPORT_BY_ID': {
      // Handle caching a single report by its statisticId
      const {statisticId, report} = action.payload;
      return {
        ...state,
        reportsByStatisticId: {
          ...state.reportsByStatisticId,
          [statisticId]: report,
        },
      };
    }

    case 'STORE_CLASS_REPORT': {
      // Handle caching a class report by classId
      const {classId, report} = action.payload;
      return {
        ...state,
        reportsByClassId: {
          ...state.reportsByClassId,
          [classId]: report,
        },
      };
    }

    case 'STORE_SCHOOL_ATTENDANCE': {
      // Handle caching school attendance by cacheKey (schoolId-month)
      const {cacheKey, report} = action.payload;
      return {
        ...state,
        schoolAttendanceByMonth: {
          ...state.schoolAttendanceByMonth,
          [cacheKey]: report,
        },
      };
    }

    case 'STORE_SCHOOL_REPORTS_SUMMARY': {
      // Handle caching school reports summary by cacheKey (schoolId-month)
      const {cacheKey, report} = action.payload;
      return {
        ...state,
        schoolReportsSummaryByMonth: {
          ...state.schoolReportsSummaryByMonth,
          [cacheKey]: report,
        },
      };
    }

    case 'STORE_SCHOOL_AGGREGATED_DATA': {
      // Handle caching school aggregated performance data by schoolId
      const {schoolId, data} = action.payload;
      return {
        ...state,
        schoolAggregatedData: {
          ...state.schoolAggregatedData,
          [schoolId]: {
            ...data,
            generatedAt: new Date().toISOString(),
          },
        },
      };
    }

    case 'STORE_CLASS_STATISTICS': {
      // Handle caching class statistics by classId
      const {classId, statistics} = action.payload;
      return {
        ...state,
        classStatisticsByClassId: {
          ...state.classStatisticsByClassId,
          [classId]: statistics,
        },
      };
    }

    case 'ADD_SCHOOL_MONTHLY_REPORT': {
      // Add or update a monthly report in the array
      const {report, selected} = action.payload;
      // Ensure array is defined
      const currentArr = state.selectedClassMonthlyReportArr || [];
      const existingIndex = currentArr.findIndex(
        (r) => r.month === report.month
      );

      let updatedArr;
      if (existingIndex >= 0) {
        // Update existing report
        updatedArr = currentArr.map((r, i) =>
          i === existingIndex ? {...report, selected} : {...r, selected: false}
        );
      } else {
        // Add new report and deselect others if this one is selected
        updatedArr = selected
          ? [
              ...currentArr.map((r) => ({
                ...r,
                selected: false,
              })),
              {...report, selected: true},
            ]
          : [...currentArr, {...report, selected: false}];
      }

      return {
        ...state,
        selectedClassMonthlyReportArr: updatedArr,
      };
    }

    case 'SET_SELECTED_MONTHLY_REPORT': {
      // Set selected flag based on month
      const {month} = action.payload;
      // Ensure array is defined
      const currentArr = state.selectedClassMonthlyReportArr || [];
      const updatedArr = currentArr.map((r) => ({
        ...r,
        selected: r.month === month,
      }));

      return {
        ...state,
        selectedClassMonthlyReportArr: updatedArr,
      };
    }

    case 'ADD_CLASS_MONTHLY_REPORT_ARRAY': {
      // Add or update a class monthly report array in the cache
      const {month, reports, selected} = action.payload;
      // Ensure array is defined
      const currentCache = state.classMonthlyReportsCache || [];
      const existingIndex = currentCache.findIndex(
        (item) => item.month === month
      );

      let updatedCache;
      if (existingIndex >= 0) {
        // Update existing month's reports
        updatedCache = currentCache.map((item, i) =>
          i === existingIndex
            ? {month, reports, selected}
            : {...item, selected: false}
        );
      } else {
        // Add new month's reports and deselect others if this one is selected
        updatedCache = selected
          ? [
              ...currentCache.map((item) => ({
                ...item,
                selected: false,
              })),
              {month, reports, selected: true},
            ]
          : [...currentCache, {month, reports, selected: false}];
      }

      return {
        ...state,
        classMonthlyReportsCache: updatedCache,
      };
    }

    case 'SET_SELECTED_CLASS_MONTHLY_REPORT_MONTH': {
      // Set selected flag based on month for class monthly reports
      const {month} = action.payload;
      // Ensure array is defined
      const currentCache = state.classMonthlyReportsCache || [];
      const updatedCache = currentCache.map((item) => ({
        ...item,
        selected: item.month === month,
      }));

      return {
        ...state,
        classMonthlyReportsCache: updatedCache,
      };
    }

    case 'STORE_CLASS_MONTHLY_REPORT_BY_MONTH': {
      // Handle caching an individual class monthly report by classId-month key
      // Current month reports are NOT cached (always fetched fresh)
      const {classId, month, report} = action.payload;
      const cacheKey = `${classId}-${month}`;
      return {
        ...state,
        classMonthlyReportsByMonth: {
          ...state.classMonthlyReportsByMonth,
          [cacheKey]: report,
        },
      };
    }

    case 'STORE_STUDENT_MONTHLY_REPORT_BY_MONTH': {
      // Handle caching an individual student monthly report by studentId-month key
      // Current month reports are NOT cached (always fetched fresh)
      const {studentId, month, report} = action.payload;
      const cacheKey = `${studentId}-${month}`;
      return {
        ...state,
        studentMonthlyReportsByMonth: {
          ...state.studentMonthlyReportsByMonth,
          [cacheKey]: report,
        },
      };
    }

    case RESET_USER:
      // Clear all cache data when user signs out
      return initialState;

    default:
      return state;
  }
};

export default fullReport;
